import YouTubeSubscribeButton from './YouTubeSubscribeButton';
import { useEffect, useRef } from 'react';
import cn from 'classnames';
import { trackVideoEvents, VideoEvent } from '@utils/gtm/ga4/trackVideoEvents';
import {
  getVideoPercent,
  // setPDPVideosCookie,
  // setPTTVideoCookie,
} from '@utils/videos';

import styles from './YouTubeEmbed.module.scss';

export type YouTubePlayer = {
  playVideo: () => void;
  unMute: () => void;
};

const YouTubeEmbed = ({
  html,
  dimensions,
  objectFit = false,
  showSubscribe,
  type,
  interactionLocation,
  isPortrait,
  aspectRatio,
  videoWrapperClassName,
  className,
  onPlayerReady,
}: {
  html: string;
  dimensions?: {
    height: number;
    width: number;
  };
  objectFit?: boolean;
  showSubscribe?: boolean;
  type?: VideoEvent['type'];
  interactionLocation?: VideoEvent['interactionLocation'];
  isPortrait?: boolean;
  aspectRatio?: number;
  videoWrapperClassName?: boolean;
  className?: string;
  onPlayerReady?: (player: YouTubePlayer) => void;
}) => {
  const playerRef = useRef<HTMLDivElement>(null);
  const lastVideoPercentRef = useRef<number>(0);
  const videoStartRef = useRef<boolean>(false);
  const onPlayerReadyRef = useRef(onPlayerReady);
  onPlayerReadyRef.current = onPlayerReady;

  useEffect(() => {
    const initPlayer = () => {
      const iframe = playerRef.current?.querySelector('iframe');
      if (
        iframe &&
        window.YT?.Player &&
        typeof window !== 'undefined' &&
        typeof window.YT !== 'undefined'
      ) {
        new window.YT.Player(iframe, {
          events: {
            onReady: (event: {
              target: {
                playVideo: () => void;
                unMute: () => void;
                playerInfo: {
                  videoUrl: string;
                  videoTitle: string;
                  duration: number;
                  videoData: {
                    title: string;
                  };
                };
              };
            }) => {
              onPlayerReadyRef.current?.(event.target);
            },
            onStateChange: (event: {
              data: number;
              target: {
                videoTitle: string;
                playerInfo: {
                  videoUrl: string;
                  duration: number;
                  currentTime: number;
                };
              };
            }) => {
              const videoProgress = Math.round(
                (event?.target?.playerInfo?.currentTime /
                  event?.target?.playerInfo?.duration) *
                  100
              );

              const videoPercent = getVideoPercent(videoProgress);

              const commonEventData = {
                path: window.location.pathname || null,
                type: type || null,
                interactionLocation: interactionLocation || null,
                videoPercent: videoPercent,
                videoDuration:
                  event?.target?.playerInfo?.duration.toFixed(0).toString() ||
                  null,
                videoTitle: event?.target?.videoTitle || null,
                videoProvider: 'Youtube' as VideoEvent['videoProvider'],
                videoUrl: event?.target?.playerInfo?.videoUrl || null,
              };

              if (event.data === window.YT.PlayerState.PLAYING) {
                if (interactionLocation && type && !videoStartRef.current) {
                  videoStartRef.current = true;
                  trackVideoEvents({
                    event: {
                      eventName: 'video_start',
                      ...commonEventData,
                    },
                  });
                  // commented this out for now as this may not be required, but to be tested ER-5564
                  // if (
                  //   interactionLocation === 'product gallery' ||
                  //   interactionLocation === 'expert advice'
                  // ) {
                  //   setPDPVideosCookie(
                  //     interactionLocation,
                  //     JSON.stringify(commonEventData)
                  //   );
                  // }
                  // if (interactionLocation === 'PTT') {
                  //   setPTTVideoCookie({
                  //     ...commonEventData,
                  //     eventName: 'video_start',
                  //   });
                  // }
                }
              }
              if (event.data === window.YT.PlayerState.PAUSED) {
                if (
                  interactionLocation &&
                  type &&
                  videoPercent &&
                  videoPercent > lastVideoPercentRef.current
                ) {
                  lastVideoPercentRef.current = videoPercent;
                  trackVideoEvents({
                    event: {
                      eventName: 'video_progress',
                      ...commonEventData,
                    },
                  });
                  // commented this out for now as this may not be required, but to be tested ER-5564
                  // if (
                  //   interactionLocation === 'product gallery' ||
                  //   interactionLocation === 'expert advice'
                  // ) {
                  //   setPDPVideosCookie(
                  //     interactionLocation,
                  //     JSON.stringify(commonEventData)
                  //   );
                  // }
                  // if (interactionLocation === 'PTT') {
                  //   setPTTVideoCookie({
                  //     ...commonEventData,
                  //     eventName: 'video_progress',
                  //   });
                  // }
                }
              }
              if (event.data === window.YT.PlayerState.ENDED) {
                if (interactionLocation && type) {
                  trackVideoEvents({
                    event: {
                      eventName: 'video_complete',
                      ...commonEventData,
                    },
                  });
                  // commented this out for now as this may not be required, but to be tested ER-5564
                  // if (
                  //   interactionLocation === 'product gallery' ||
                  //   interactionLocation === 'expert advice'
                  // ) {
                  //   setPDPVideosCookie(
                  //     interactionLocation,
                  //     JSON.stringify(commonEventData)
                  //   );
                  // }
                  // if (interactionLocation === 'PTT') {
                  //   setPTTVideoCookie({
                  //     ...commonEventData,
                  //     eventName: 'video_complete',
                  //   });
                  // }
                }
              }
            },
          },
        });
      }
    };

    // Load YouTube API if not already loaded
    if (typeof window !== 'undefined') {
      if (typeof window.YT === 'undefined') {
        window.onYouTubeIframeAPIReady = initPlayer;

        if (!document.querySelector('script[src*="youtube.com/iframe_api"]')) {
          const tag = document.createElement('script');
          tag.src = 'https://www.youtube.com/iframe_api';
          document.head.appendChild(tag);
        }
      } else {
        initPlayer();
      }
    }

    return () => {
      window.onYouTubeIframeAPIReady = () => {};
    };
  }, [interactionLocation, type]);

  const dimensionsStyle = dimensions
    ? {
        paddingBottom: `calc(100% * ${dimensions?.height}/${dimensions?.width})`,
      }
    : undefined;

  const objectFitStyle = objectFit
    ? {
        width: '100%',
        height: '100%',
        objectFit: 'cover' as const,
      }
    : undefined;

  const videWrapperStyle = videoWrapperClassName
    ? {
        paddingBottom: `${aspectRatio}%`,
      }
    : undefined;

  return (
    <>
      <div
        ref={playerRef}
        style={{
          ...dimensionsStyle,
          ...objectFitStyle,
          ...videWrapperStyle,
        }}
        data-testid="data-youtube-media-embed"
        className={cn(
          dimensions ? styles.youtubeContainer : undefined,
          className,
          {
            [styles.portrait]: isPortrait,
            [styles.videoWrapperClassName]: videoWrapperClassName,
          }
        )}
        dangerouslySetInnerHTML={{ __html: html }}
      />
      {showSubscribe && (
        <YouTubeSubscribeButton channel="mecvideos" count="default" />
      )}
    </>
  );
};
export default YouTubeEmbed;
